import "./PriceCategoryMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

// ─────────────────────────────────────────────────────────
//  PRICE CATEGORY MASTER COMPONENT
// ─────────────────────────────────────────────────────────
export default function PriceCategoryMaster() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [selectedPriceCategory, setSelectedPriceCategory] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [priceCategories, setPriceCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    description: "",
    active: 1,
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── GET /price-category/list/all ───────────────────────────────────
  const getPriceCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/price-category/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      // Handle response - map API fields to component fields
      const data = response.data?.data || response.data || [];
      const mappedCategories = data.map((item) => ({
        id: item.id || item.category_id,
        description: item.description,
        active: item.is_active === true || item.is_active === 1,
      }));

      setPriceCategories(mappedCategories);
    } catch (err) {
      console.error("Failed to load price categories:", err.response?.data);
      toastError("Failed to load price categories");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    getPriceCategories();
  }, [getPriceCategories]);

  // ─── GET /price-category/{category_id} ───────────────────────────────────
  const getPriceCategoryById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/price-category/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;
        return {
          id: data.id || data.category_id,
          description: data.description,
          active: data.is_active === true || data.is_active === 1,
        };
      } catch (err) {
        console.error("Failed to fetch price category:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── POST /price-category/add ───────────────────────────────────
  const createPriceCategory = async (categoryData) => {
    try {
      const response = await axios.post(
        `${API_URL}/price-category/add`,
        {
          description: categoryData.description,
          is_active: categoryData.active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create error:", err.response?.data);
      throw err;
    }
  };

  // ─── PUT /price-category/update/{category_id} ────────────────────────────
  const updatePriceCategory = async (id, categoryData) => {
    try {
      const response = await axios.put(
        `${API_URL}/price-category/update/${id}`,
        {
          description: categoryData.description,
          is_active: categoryData.active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  // ─── Open Edit ───────────────────────────────────────────────────
  const openEdit = async (priceCategory) => {
    setFormError("");
    setIsEditMode(true);
    // setSelectedPriceCategory(priceCategory);
    setEditingId(priceCategory.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const categoryData = await getPriceCategoryById(priceCategory.id);
      setFormData({
        description: categoryData.description || "",
        active: categoryData.active ? 1 : 0,
      });
    } catch (err) {
      const msg = "Failed to load price category details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const openAdd = () => {
    setIsEditMode(false);
    // setSelectedPriceCategory(null);
    setEditingId(null);
    setFormData({ description: "", active: 1 });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData({ description: "", active: 1 });
    setFormError("");
    // setSelectedPriceCategory(null);
    setEditingId(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
          ? 1
          : 0
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  // ─── Handle Submit (Create/Update) ───────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");
    if (!formData.description.trim()) {
      const msg = "Price category description is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    setFormLoading(true);

    try {
      if (isEditMode) {
        // Update existing price category
        await updatePriceCategory(editingId, formData);

        // Update local state
        setPriceCategories((prev) =>
          prev.map((p) =>
            p.id === editingId
              ? {
                  ...p,
                  description: formData.description,
                  active: formData.active === 1,
                }
              : p,
          ),
        );

        toastSuccess("Price category updated successfully.");
      } else {
        // Create new price category
        await createPriceCategory(formData);

        // Refresh the list
        await getPriceCategories();

        toastSuccess("Price category created successfully.");
      }

      closeForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save price category.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Toggle Price Category Status ──────────────────────────────
  const togglePriceCategoryStatus = async (priceCategory) => {
    const newStatus = priceCategory.active ? 0 : 1;

    try {
      await updatePriceCategory(priceCategory.id, {
        description: priceCategory.description,
        active: newStatus,
      });

      // Update local state
      setPriceCategories((prev) =>
        prev.map((p) =>
          p.id === priceCategory.id ? { ...p, active: newStatus === 1 } : p,
        ),
      );

      toastSuccess(
        newStatus === 1
          ? "Price category activated successfully."
          : "Price category deactivated successfully.",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update price category status.";
      toastError(msg);
    }
  };

  // ── Filter price categories ──
  const filteredPriceCategories = priceCategories.filter((priceCategory) =>
    priceCategory.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ════════════════════════════════════════════════════
  //  ADD / EDIT FORM
  // ════════════════════════════════════════════════════
  if (showForm) {
    return (
      <div className="pcm-form-page">
        {/* Header with Back Arrow */}
        <div className="pcm-form-header">
          <div className="pcm-form-header-left">
            <button className="pcm-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="pcm-back-icon" />
            </button>
            <div>
              <h2 className="pcm-form-title">
                {isEditMode ? "Edit Price Category" : "Add Price Category"}
              </h2>
              <p className="pcm-form-subtitle">
                {isEditMode
                  ? "Update price category description and status"
                  : "Create a new price category classification"}
              </p>
            </div>
          </div>
        </div>

        <div className="pcm-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="pcm-form-grid">
            {/* Description */}
            <div className="pcm-form-group">
              <label>Price Category Description *</label>
              <input
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter price category description"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>

            {/* Status — only show in edit mode */}
            {isEditMode && (
              <div className="pcm-form-group">
                <label>Status</label>
                <div className="pcm-status-toggle">
                  <label className="pcm-switch">
                    <input
                      type="checkbox"
                      checked={formData.active === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          active: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <span className="pcm-slider"></span>
                  </label>
                  <span className="pcm-status-label">
                    {formData.active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="pcm-form-footer">
            <button className="pcm-btn pcm-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="pcm-btn pcm-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Price Category"
                  : "Save Price Category"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  PRICE CATEGORY LIST
  // ════════════════════════════════════════════════════
  return (
    <div className="pcm-list-page">
      {/* ─── Top Bar ─── */}
      <div className="pcm-page-header">
        <div>
          <h1 className="pcm-page-title">Price Category Master</h1>
          <p className="pcm-page-subtitle">
            Manage price categories and classifications
          </p>
        </div>
        <div className="pcm-page-actions">
          <div className="pcm-search-box">
            <span className="pcm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search price categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="pcm-btn pcm-btn-primary" onClick={openAdd}>
            + Add Price Category
          </button>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="pcm-table-card">
        <div className="pcm-table-responsive">
          <table className="pcm-table">
            <thead>
              <tr>
                <th className="pcm-col-id">#</th>
                <th className="pcm-col-description">Description</th>
                <th className="pcm-col-active">Status</th>
                <th className="pcm-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="pcm-empty-row">
                    Loading price categories...
                  </td>
                </tr>
              ) : filteredPriceCategories.length === 0 ? (
                <tr>
                  <td colSpan="4">
                    <div className="pcm-empty-state">
                      <div className="pcm-empty-icon">💰</div>
                      <div className="pcm-empty-title">
                        No Price Categories Found
                      </div>
                      <div className="pcm-empty-desc">
                        {searchTerm
                          ? `No price categories match "${searchTerm}"`
                          : "Start by adding your first price category"}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPriceCategories.map((priceCategory, index) => (
                  <tr key={priceCategory.id}>
                    <td className="pcm-col-id">{index + 1}</td>
                    <td className="pcm-col-description">
                      {priceCategory.description}
                    </td>
                    <td className="pcm-col-active">
                      <label className="pcm-switch">
                        <input
                          type="checkbox"
                          checked={priceCategory.active}
                          onChange={() =>
                            togglePriceCategoryStatus(priceCategory)
                          }
                        />
                        <span className="pcm-slider"></span>
                      </label>
                    </td>
                    <td className="pcm-col-actions">
                      <div className="pcm-action-group">
                        <button
                          className="pcm-action-btn pcm-action-btn-edit"
                          onClick={() => openEdit(priceCategory)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="pcm-action-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
